import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface AdminStats {
  totalLawsuits: number;
  activeLawsuits: number;
  totalUsers: number;
  totalRevenue: number;
  pendingApprovals: number;
}

interface RecentLawsuit {
  id: string;
  title: string;
  status: 'pending' | 'active' | 'completed';
  participants: number;
  createdAt: string;
  revenue: number;
}

interface RecentUser {
  id: string;
  name: string;
  email: string;
  joinedAt: string;
  lawsuitsCount: number;
}

const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentLawsuits, setRecentLawsuits] = useState<RecentLawsuit[]>([]);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // const [activeTab, setActiveTab] = useState<"overview" | "lawsuits" | "users" | "analytics">("overview");

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setIsLoading(true);
        // TODO: Replace with actual API calls
        // const [statsRes, lawsuitsRes, usersRes] = await Promise.all([
        //   axios.get('/api/admin/stats'),
        //   axios.get('/api/admin/recent-lawsuits'),
        //   axios.get('/api/admin/recent-users')
        // ]);

        // Mock data for now
        const mockStats: AdminStats = {
          totalLawsuits: 156,
          activeLawsuits: 42,
          totalUsers: 2847,
          totalRevenue: 1250000,
          pendingApprovals: 8
        };

        const mockLawsuits: RecentLawsuit[] = [
          {
            id: '1',
            title: 'Class Action Against Tech Corp Data Breach',
            status: 'active',
            participants: 45,
            createdAt: '2024-01-15',
            revenue: 22500
          },
          {
            id: '2',
            title: 'Consumer Rights Violation Case',
            status: 'pending',
            participants: 12,
            createdAt: '2024-01-14',
            revenue: 6000
          },
          {
            id: '3',
            title: 'Employment Discrimination Lawsuit',
            status: 'completed',
            participants: 78,
            createdAt: '2024-01-10',
            revenue: 39000
          }
        ];

        const mockUsers: RecentUser[] = [
          {
            id: '1',
            name: 'John Doe',
            email: 'john@example.com',
            joinedAt: '2024-01-15',
            lawsuitsCount: 2
          },
          {
            id: '2',
            name: 'Jane Smith',
            email: 'jane@example.com',
            joinedAt: '2024-01-14',
            lawsuitsCount: 1
          },
          {
            id: '3',
            name: 'Ahmed Al-Hassan',
            email: 'ahmed@example.com',
            joinedAt: '2024-01-13',
            lawsuitsCount: 0
          }
        ];

        setStats(mockStats);
        setRecentLawsuits(mockLawsuits);
        setRecentUsers(mockUsers);
      } catch (err) {
        console.error('Failed to fetch admin data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { class: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      active: { class: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      completed: { class: 'bg-blue-100 text-blue-800', icon: CheckCircleIcon }
    };

    const statusInfo = statusMap[status as keyof typeof statusMap] || statusMap.pending;
    const StatusIcon = statusInfo.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.class}`}>
        <StatusIcon className="h-3 w-3 mr-1" />
        {t(`lawsuit.status.${status}`, status)}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('admin.accessDenied', 'Access Denied')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('admin.adminRequired', 'You need admin privileges to access this page.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('admin.dashboard', 'Admin Dashboard')} - tvia.ai</title>
        <meta name="description" content={t('admin.dashboardDescription', 'Admin dashboard for managing the tvia.ai platform')} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {t('admin.dashboard', 'Admin Dashboard')}
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              {t('admin.welcomeMessage', 'Welcome to the admin dashboard. Here you can manage all aspects of the platform.')}
            </p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('admin.totalLawsuits', 'Total Lawsuits')}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.totalLawsuits}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('admin.activeLawsuits', 'Active Lawsuits')}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.activeLawsuits}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UserGroupIcon className="h-6 w-6 text-blue-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('admin.totalUsers', 'Total Users')}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.totalUsers.toLocaleString()}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('admin.totalRevenue', 'Total Revenue')}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {formatCurrency(stats.totalRevenue)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {t('admin.pendingApprovals', 'Pending Approvals')}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stats.pendingApprovals}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Lawsuits */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('admin.recentLawsuits', 'Recent Lawsuits')}
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {recentLawsuits.map((lawsuit) => (
                    <div key={lawsuit.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {lawsuit.title}
                        </h3>
                        <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                          <span>{lawsuit.participants} {t('lawsuit.participants', 'participants')}</span>
                          <span>{formatCurrency(lawsuit.revenue)}</span>
                          <span>{new Date(lawsuit.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="mt-2">
                          {getStatusBadge(lawsuit.status)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <button className="text-cyan-600 hover:text-cyan-700">
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <button className="w-full text-center text-sm text-cyan-600 hover:text-cyan-700">
                    {t('admin.viewAllLawsuits', 'View All Lawsuits')}
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Users */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('admin.recentUsers', 'Recent Users')}
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {recentUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-cyan-100 flex items-center justify-center">
                          <span className="text-xs font-medium text-cyan-800">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-gray-900">
                            {user.name}
                          </h3>
                          <p className="text-xs text-gray-500">{user.email}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                            <span>{t('admin.joined', 'Joined')} {new Date(user.joinedAt).toLocaleDateString()}</span>
                            <span>{user.lawsuitsCount} {t('lawsuit.lawsuits', 'lawsuits')}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <button className="text-cyan-600 hover:text-cyan-700">
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <button className="w-full text-center text-sm text-cyan-600 hover:text-cyan-700">
                    {t('admin.viewAllUsers', 'View All Users')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('admin.quickActions', 'Quick Actions')}
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-cyan-600 hover:bg-cyan-700 min-h-[48px] transition-all">
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  {t('admin.approveLawsuit', 'Approve Pending Lawsuits')}
                </button>
                <button className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 min-h-[48px] transition-all">
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  {t('admin.manageUsers', 'Manage Users')}
                </button>
                <button className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 min-h-[48px] transition-all">
                  <ChartBarIcon className="h-5 w-5 mr-2" />
                  {t('admin.viewAnalytics', 'View Analytics')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboardPage; 